// ===== API SERVICE =====
// Conecta com o backend Cloudflare Workers

import Storage from './storage';
import { API_BASE_URL, TOKEN_EXPIRY_BUFFER_MS } from '@constants/api';
import { captureAcervoEvent } from '@services/posthog';

function normalizeLegacyAnalyticsDashboard(legacy) {
  const usage = legacy?.uso_acervo || {};
  const usageSummary = usage.resumo || {};
  const attendance = legacy?.ensaios || {};
  const attendanceSummary = attendance.resumo || {};
  const sheetRanking = (usage.top_partituras || []).map((item, index) => ({
    ...item,
    posicao: index + 1,
    acessos_pdf: Number(item.visualizacoes || 0) + Number(item.downloads || 0),
    usuarios: 0,
    instrumentos_explorados: 0,
  }));
  const engagementRanking = (usage.ranking_musicos || []).map((item, index) => ({
    ...item,
    posicao: index + 1,
    total_acoes: Number(item.visualizacoes || 0) + Number(item.downloads || 0) + Number(item.buscas || 0),
    favoritos: 0,
    repertorios: 0,
    dias_ativos: 0,
    sessoes: 0,
  }));
  const summarySheets = sheetRanking.reduce((summary, item) => ({
    visualizacoes: summary.visualizacoes + Number(item.visualizacoes || 0),
    downloads: summary.downloads + Number(item.downloads || 0),
    acessos_pdf: summary.acessos_pdf + Number(item.acessos_pdf || 0),
    usuarios: summary.usuarios + Number(item.usuarios || 0),
    partituras_com_acao: summary.partituras_com_acao + 1,
  }), { visualizacoes: 0, downloads: 0, acessos_pdf: 0, usuarios: 0, partituras_com_acao: 0 });

  return {
    periodo: legacy.periodo || null,
    resumo: {
      engajamento: {
        total_acoes: engagementRanking.reduce((total, item) => total + Number(item.total_acoes || 0), 0),
        visualizacoes: Number(usageSummary.pdfs_visualizados || 0),
        downloads: Number(usageSummary.downloads_reais || 0),
        buscas: engagementRanking.reduce((total, item) => total + Number(item.buscas || 0), 0),
        favoritos: 0,
        repertorios: 0,
        usuarios_com_acao: engagementRanking.length,
        usuarios_elegiveis: engagementRanking.length,
        variacao_acoes: null,
      },
      partituras: { ...summarySheets, variacao_acessos: null },
      assiduidade: {
        ensaios_realizados: Number(attendanceSummary.ensaios_registrados || 0),
        presencas_total: 0,
        presencas_esperadas: 0,
        taxa_media: attendanceSummary.presenca_media ?? null,
        musicos_com_presenca: 0,
        sem_ensaios: Number(attendanceSummary.ensaios_registrados || 0) === 0,
      },
    },
    insights: usage.insights || [],
    rankings: {
      engajamento: engagementRanking,
      partituras: sheetRanking,
      assiduidade: (attendance.assiduidade_musicos || []).map((item, index) => ({ ...item, posicao: item.posicao || index + 1 })),
    },
    projecoes: null,
    amostras: {},
    _legacy: legacy,
  };
}

// Callback para notificar quando token expirar
let onTokenExpired = null;

export const API = {
  // Registra callback para expiração de token
  setOnTokenExpired(callback) {
    onTokenExpired = callback;
  },

  // Verifica se o token JWT expirou
  isTokenExpired() {
    const token = Storage.get('authToken', null);
    const expiresAt = Storage.get('tokenExpiresAt', null);

    // Se não há token, não está expirado (não está logado)
    if (!token) return false;

    // Se há token mas não há expiresAt, considera expirado
    // (token antigo sem controle de expiração)
    if (!expiresAt) return true;

    // Considera expirado alguns minutos antes para evitar problemas
    return Date.now() > (expiresAt - TOKEN_EXPIRY_BUFFER_MS);
  },

  // Limpa autenticação
  clearAuth() {
    Storage.remove('authToken');
    Storage.remove('tokenExpiresAt');
    Storage.remove('trackingSessionId');
    Storage.remove('user');
  },

  // Helper para fazer requisicoes
  async request(endpoint, options = {}, internal = {}) {
    const { skipAuthCheck = false } = internal;
    try {
      // Verifica expiração do token apenas para requisições autenticadas
      if (!skipAuthCheck && this.isTokenExpired()) {
        this.clearAuth();
        if (onTokenExpired) {
          onTokenExpired();
        }
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const token = Storage.get('authToken', null);
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers
      });

      // Se receber 401, token expirou ou é inválido - limpa sessão
      if (response.status === 401) {
        const error = await response.json().catch(() => ({ error: 'Não autorizado' }));
        // Qualquer 401 indica que o token não é mais válido
        this.clearAuth();
        if (onTokenExpired) {
          onTokenExpired();
        }
        throw new Error(error.error || 'Sessão expirada. Faça login novamente.');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  },

  // ============ PARTITURAS ============

  async getPartituras(filters = {}) {
    const params = new URLSearchParams();
    if (filters.categoria) params.append('categoria', filters.categoria);
    if (filters.busca) params.append('busca', filters.busca);
    if (filters.destaque) params.append('destaque', '1');

    const query = params.toString();
    return this.request(`/api/partituras${query ? `?${query}` : ''}`);
  },

  async getPartitura(id) {
    return this.request(`/api/partituras/${id}`);
  },

  async createPartitura(formData) {
    const token = Storage.get('authToken', null);
    const response = await fetch(`${API_BASE_URL}/api/partituras`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro no upload' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  async updatePartitura(id, data) {
    return this.request(`/api/partituras/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async getPartituraAudioAccess(id) {
    return this.request(`/api/partituras/${id}/audio/access`);
  },

  async uploadPartituraAudio(id, file) {
    const token = Storage.get('authToken', null);
    const form = new FormData();
    form.append('audio', file);
    const response = await fetch(`${API_BASE_URL}/api/partituras/${id}/audio`, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Não foi possível enviar o áudio');
    return result;
  },

  async removePartituraAudio(id) {
    return this.request(`/api/partituras/${id}/audio`, { method: 'DELETE' });
  },

  async updatePartituraYoutube(id, youtubeUrl) {
    return this.request(`/api/partituras/${id}/youtube`, {
      method: 'PUT',
      body: JSON.stringify({ youtube_url: youtubeUrl })
    });
  },

  async deletePartitura(id) {
    return this.request(`/api/partituras/${id}`, {
      method: 'DELETE'
    });
  },

  async uploadPastaPartitura(formData) {
    const token = Storage.get('authToken', null);
    const response = await fetch(`${API_BASE_URL}/api/partituras/upload-pasta`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro no upload' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  async getPartesPartitura(partituraId) {
    return this.request(`/api/partituras/${partituraId}/partes`);
  },

  async addPartePartitura(partituraId, formData) {
    const token = Storage.get('authToken', null);
    const response = await fetch(`${API_BASE_URL}/api/partituras/${partituraId}/partes`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao adicionar parte' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  async replacePartePartitura(partituraId, parteId, formData) {
    const token = Storage.get('authToken', null);
    // Endpoint correto: PUT /api/partes/:id/substituir (partituraId não é usado)
    const response = await fetch(`${API_BASE_URL}/api/partes/${parteId}/substituir`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao substituir parte' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  async deletePartePartitura(partituraId, parteId) {
    // Endpoint correto: DELETE /api/partes/:id (partituraId não é usado)
    return this.request(`/api/partes/${parteId}`, {
      method: 'DELETE'
    });
  },

  async renomearPartePartitura(parteId, instrumento) {
    return this.request(`/api/partes/${parteId}/renomear`, {
      method: 'PUT',
      body: JSON.stringify({ instrumento })
    });
  },

  async corrigirBombardinosPartitura(partituraId, formData) {
    const token = Storage.get('authToken', null);
    const response = await fetch(`${API_BASE_URL}/api/partituras/${partituraId}/corrigir-bombardinos`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao corrigir bombardinos' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  // ============ DOWNLOAD ============

  getDownloadUrl(id, instrumento = null) {
    let url = `${API_BASE_URL}/api/download/${id}`;
    if (instrumento) url += `?instrumento=${instrumento}`;
    return url;
  },

  getDownloadParteUrl(parteId) {
    return `${API_BASE_URL}/api/download/parte/${parteId}`;
  },

  // ============ CATEGORIAS E INSTRUMENTOS ============

  async getCategorias() {
    return this.request('/api/categorias');
  },

  async getInstrumentos() {
    return this.request('/api/instrumentos');
  },

  // ============ ESTATISTICAS ============

  async getEstatisticas() {
    return this.request('/api/estatisticas');
  },

  // ============ ATIVIDADES ============

  async getAtividades() {
    return this.request('/api/atividades');
  },

  async getMinhasAtividades() {
    return this.request('/api/minhas-atividades');
  },

  // ============ AUTH ============

  async login(username, pin, rememberMe = false) {
    const result = await this.request('/api/login', {
      method: 'POST',
      body: JSON.stringify({ username, pin, rememberMe })
    });

    if (result.token) {
      Storage.set('authToken', result.token);

      // Armazena tempo de expiração (expiresIn em segundos)
      if (result.expiresIn) {
        const expiresAt = Date.now() + (result.expiresIn * 1000);
        Storage.set('tokenExpiresAt', expiresAt);
      }

      if (result.tracking_session_id) {
        Storage.set('trackingSessionId', result.tracking_session_id);
      }
    }

    return result;
  },

  async changePin(currentPin, newPin) {
    try {
      const result = await this.request('/api/change-pin', {
        method: 'POST',
        body: JSON.stringify({ currentPin, newPin })
      });

      // Se a mudança de PIN retornou novo token, atualiza
      if (result.token) {
        Storage.set('authToken', result.token);
        // Token tem mesma duração de 24h
        const expiresAt = Date.now() + (24 * 60 * 60 * 1000);
        Storage.set('tokenExpiresAt', expiresAt);
      }

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async logout() {
    try {
      await this.endTrackingSession();
    } catch (error) {
      console.error('Erro ao encerrar sessao de tracking:', error);
    } finally {
      this.clearAuth();
    }
  },

  // ============ HEALTH CHECK ============

  async healthCheck() {
    try {
      const result = await this.request('/api/health');
      return result.status === 'ok';
    } catch {
      return false;
    }
  },

  // Obter versão atual do servidor
  async getVersion() {
    try {
      return await this.request('/api/version');
    } catch {
      return null;
    }
  },

  // ============ PERFIL ============

  async getPerfil() {
    return this.request('/api/perfil');
  },

  async getPresencaStats() {
    // Fail silently — endpoint pode não existir em produção ainda
    // Exceto 401: token expirado deve disparar logout normal
    try {
      const token = Storage.get('authToken', null);
      const res = await fetch(`${API_BASE_URL}/api/presenca/minhas/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 401) {
        this.clearAuth();
        if (onTokenExpired) onTokenExpired();
        return null;
      }
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  },

  async uploadFotoPerfil(file) {
    const token = Storage.get('authToken', null);
    const formData = new FormData();
    formData.append('foto', file);
    const res = await fetch(`${API_BASE_URL}/api/perfil/foto`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 401) {
      this.clearAuth();
      if (onTokenExpired) onTokenExpired();
      throw new Error('Sessão expirada');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao fazer upload da foto');
    }
    return res.json();
  },

  async updatePerfil(data) {
    return this.request('/api/perfil', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // ============ FAVORITOS ============

  async getFavoritos() {
    return this.request('/api/favoritos');
  },

  async getFavoritosIds() {
    return this.request('/api/favoritos/ids');
  },

  async addFavorito(partituraId) {
    return this.request(`/api/favoritos/${partituraId}`, {
      method: 'POST'
    });
  },

  async removeFavorito(partituraId) {
    return this.request(`/api/favoritos/${partituraId}`, {
      method: 'DELETE'
    });
  },

  // ============ USUARIOS (ADMIN) ============

  async getUsuarios() {
    return this.request('/api/usuarios');
  },

  async getUsuario(id) {
    return this.request(`/api/usuarios/${id}`);
  },

  async createUsuario(data) {
    return this.request('/api/usuarios', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateUsuario(id, data) {
    return this.request(`/api/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteUsuario(id) {
    return this.request(`/api/usuarios/${id}`, {
      method: 'DELETE'
    });
  },

  // ============ CATEGORIAS (ADMIN) ============

  async createCategoria(data) {
    return this.request('/api/categorias', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateCategoria(id, data) {
    return this.request(`/api/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteCategoria(id) {
    return this.request(`/api/categorias/${id}`, {
      method: 'DELETE'
    });
  },

  async reorderCategorias(ordens) {
    return this.request('/api/categorias/reorder', {
      method: 'POST',
      body: JSON.stringify({ ordens })
    });
  },

  // ============ ESTATISTICAS ADMIN ============

  async getEstatisticasAdmin() {
    return this.request('/api/admin/estatisticas');
  },

  async getAnalyticsDashboard(queryString = '') {
    return this.request(`/api/admin/analytics/dashboard${queryString}`);
  },

  async getAnalyticsOverview(queryString = '') {
    try {
      return await this.request(`/api/admin/analytics/overview${queryString}`);
    } catch (error) {
      if (error.message !== 'Endpoint não encontrado') throw error;
      const legacy = await this.getAnalyticsDashboard(queryString);
      return normalizeLegacyAnalyticsDashboard(legacy);
    }
  },

  async getAnalyticsDetail(view, queryString = '') {
    const params = queryString ? `${queryString}&view=${encodeURIComponent(view)}` : `?view=${encodeURIComponent(view)}`;
    try {
      return await this.request(`/api/admin/analytics/detail${params}`);
    } catch (error) {
      if (error.message !== 'Endpoint não encontrado') throw error;
      const legacy = normalizeLegacyAnalyticsDashboard(await this.getAnalyticsDashboard(queryString));
      const detailMap = {
        engajamento: legacy._legacy?.uso_acervo ? {
          ...legacy.resumo.engajamento,
          ranking: legacy.rankings.engajamento,
          tendencia: [],
          amostras: {},
        } : {},
        partituras: legacy._legacy?.uso_acervo ? {
          ...legacy.resumo.partituras,
          ranking: legacy.rankings.partituras,
          partes: legacy._legacy.uso_acervo.top_partes || [],
          tendencia: [],
          amostras: {},
        } : {},
        assiduidade: {
          ...legacy.resumo.assiduidade,
          ranking: legacy.rankings.assiduidade,
          naipes: legacy._legacy?.ensaios?.presenca_naipes || [],
          tendencia: legacy._legacy?.tendencia_presenca || [],
          amostra: {},
        },
      };
      return { [view]: detailMap[view], periodo: legacy.periodo, projecao: null, insights: legacy.insights };
    }
  },

  async getAuditActivities(queryString = '') {
    try {
      return await this.request(`/api/admin/auditoria${queryString}`);
    } catch (error) {
      if (error.message !== 'Endpoint não encontrado') throw error;
      const legacy = await this.getAnalyticsDashboard(`${queryString}${queryString ? '&' : '?'}section=alteracoes`);
      return {
        periodo: legacy.periodo || null,
        usuarios: legacy.alteracoes?.usuarios || [],
        atividades: legacy.alteracoes?.atividades || legacy.atividade_recente || [],
        total: legacy.alteracoes?.total || legacy.total_atividades || 0,
      };
    }
  },

  async trackSearch(termo, resultadosCount) {
    // Tracking silencioso (sem await no fetch se possível, mas aqui usamos helpers)
    // Não usar this.request para não lançar erro global
    try {
      const token = Storage.get('authToken', null);
      await fetch(`${API_BASE_URL}/api/tracking/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ termo, resultados_count: resultadosCount })
      });
    } catch {
      // Ignorar erros de tracking
    }
  },

  async trackEvent(event) {
    try {
      const token = Storage.get('authToken', null);
      if (!token) return;

      const trackingSessionId = Storage.get('trackingSessionId', null);
      const response = await fetch(`${API_BASE_URL}/api/tracking/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...(trackingSessionId && { 'X-Tracking-Session': trackingSessionId })
        },
        body: JSON.stringify(event)
      });

      const result = await response.json().catch(() => null);
      if (result?.session_id) {
        Storage.set('trackingSessionId', result.session_id);
      }

      if (response.ok) captureAcervoEvent(event);
    } catch {
      // Ignorar erros de tracking
    }
  },

  // ============ MANUTENÇÃO ADMIN ============

  async endTrackingSession() {
    try {
      const token = Storage.get('authToken', null);
      const trackingSessionId = Storage.get('trackingSessionId', null);
      if (!token || !trackingSessionId) return;

      await fetch(`${API_BASE_URL}/api/tracking/session/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tracking-Session': trackingSessionId
        },
        body: JSON.stringify({ session_id: trackingSessionId })
      });
    } catch {
      // Ignorar erros de tracking
    }
  },

  async limparNomesUsuarios() {
    return this.request('/api/admin/manutencao/limpar-nomes', {
      method: 'POST'
    });
  },

  async configurarSuperAdmin(data) {
    return this.request('/api/admin/manutencao/super-admin', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // ============ REPERTORIO ============

  async getRepertorioAtivo(all = false) {
    return this.request(`/api/repertorio/ativo${all ? '?all=true' : ''}`);
  },

  async getRepertorio(id) {
    return this.request(`/api/repertorio/${id}`);
  },

  async getRepertorioInstrumentos(id) {
    return this.request(`/api/repertorio/${id}/instrumentos`);
  },

  async getRepertorioDownloadAvailability(id, instrumento, partituraIds = null) {
    const params = new URLSearchParams();
    if (instrumento) params.set('instrumento', instrumento);
    if (partituraIds?.length) params.set('partituras', partituraIds.join(','));

    return this.request(
      `/api/repertorio/${id}/disponibilidade-download?${params.toString()}`
    );
  },

  async getRepertorios() {
    return this.request('/api/repertorios');
  },

  async createRepertorio(data) {
    return this.request('/api/repertorios', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateRepertorio(id, data) {
    return this.request(`/api/repertorio/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteRepertorio(id) {
    return this.request(`/api/repertorio/${id}`, {
      method: 'DELETE'
    });
  },

  async addPartituraToRepertorio(repertorioId, partituraId) {
    return this.request(`/api/repertorio/${repertorioId}/partituras`, {
      method: 'POST',
      body: JSON.stringify({ partitura_id: partituraId })
    });
  },

  async removePartituraFromRepertorio(repertorioId, partituraId) {
    return this.request(`/api/repertorio/${repertorioId}/partituras/${partituraId}`, {
      method: 'DELETE'
    });
  },

  async reorderRepertorioPartituras(repertorioId, ordens) {
    return this.request(`/api/repertorio/${repertorioId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ ordens })
    });
  },

  async duplicarRepertorio(id) {
    return this.request(`/api/repertorio/${id}/duplicar`, {
      method: 'POST'
    });
  },

  async isPartituraInRepertorio(partituraId) {
    return this.request(`/api/partituras/${partituraId}/in-repertorio`);
  },

  getRepertorioDownloadUrl(id, instrumento, formato = 'pdf', partituraIds = null) {
    let url = `${API_BASE_URL}/api/repertorio/${id}/download?formato=${formato}`;
    if (instrumento) url += `&instrumento=${encodeURIComponent(instrumento)}`;
    if (partituraIds && partituraIds.length > 0) {
      url += `&partituras=${partituraIds.join(',')}`;
    }
    return url;
  },

  // ============ CONFIGURAÇÕES ============

  async getModoRecesso() {
    try {
      return await this.request('/api/config/recesso', {}, { skipAuthCheck: true });
    } catch {
      return { ativo: false };
    }
  },

  async setModoRecesso(ativo) {
    return this.request('/api/config/recesso', {
      method: 'PUT',
      body: JSON.stringify({ ativo })
    });
  },

  async getTutoriaisAtivos() {
    try {
      return await this.request('/api/config/tutoriais', {}, { skipAuthCheck: true });
    } catch {
      return { ativo: true };
    }
  },

  async setTutoriaisAtivos(ativo) {
    return this.request('/api/config/tutoriais', {
      method: 'PUT',
      body: JSON.stringify({ ativo })
    });
  },

  async getDiasEnsaio() {
    return this.request('/api/config/dias-ensaio', {}, { skipAuthCheck: true });
  },

  async setDiasEnsaio(dias, hora) {
    return this.request('/api/config/dias-ensaio', {
      method: 'PUT',
      body: JSON.stringify({ dias, hora })
    });
  },

  // ============ PRESENÇA ============

  async getMinhaPresenca() {
    return this.request('/api/presenca/minhas');
  },

  async registrarPresencas(dataEnsaio, usuariosIds) {
    return this.request('/api/presenca', {
      method: 'POST',
      body: JSON.stringify({
        data_ensaio: dataEnsaio,
        usuarios_ids: usuariosIds
      })
    });
  },

  async getTodasPresencas() {
    return this.request('/api/presenca/todas');
  },

  async getDetalheEnsaio(dataEnsaio) {
    return this.request(`/api/presenca/${dataEnsaio}`);
  },

  async removerPresenca(dataEnsaio, usuarioId) {
    return this.request(`/api/presenca/${dataEnsaio}/usuario/${usuarioId}`, {
      method: 'DELETE'
    });
  },

  async excluirEnsaio(dataEnsaio) {
    return this.request(`/api/presenca/${dataEnsaio}`, {
      method: 'DELETE'
    });
  },

  // ============ ENSAIOS ============

  async getPartiturasEnsaio(dataEnsaio) {
    return this.request(`/api/ensaios/${dataEnsaio}/partituras`);
  },

  async updateEnsaioConfig(dataEnsaio, youtubeUrl) {
    let normalizedUrl = youtubeUrl;
    if (youtubeUrl) {
      normalizedUrl = youtubeUrl.trim();
      if (normalizedUrl) {
        let parsedUrl;
        try {
          parsedUrl = new URL(normalizedUrl);
        } catch {
          throw new Error('Informe um link válido do YouTube');
        }
        const host = parsedUrl.hostname.replace(/^www\./, '');
        const allowedHosts = new Set(['youtube.com', 'm.youtube.com', 'youtu.be']);
        if (!['http:', 'https:'].includes(parsedUrl.protocol) || !allowedHosts.has(host)) {
          throw new Error('Informe um link válido do YouTube');
        }
      }
    }
    return this.request(`/api/ensaios/${dataEnsaio}/config`, {
      method: 'PATCH',
      body: JSON.stringify({ youtube_url: normalizedUrl || null })
    });
  },

  async addPartituraEnsaio(dataEnsaio, partituraId) {
    return this.request(`/api/ensaios/${dataEnsaio}/partituras`, {
      method: 'POST',
      body: JSON.stringify({ partitura_id: partituraId })
    });
  },

  async removePartituraEnsaio(dataEnsaio, id) {
    return this.request(`/api/ensaios/${dataEnsaio}/partituras/${id}`, {
      method: 'DELETE'
    });
  },

  async reorderPartiturasEnsaio(dataEnsaio, ordens) {
    return this.request(`/api/ensaios/${dataEnsaio}/partituras/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ ordens })
    });
  },

  // ============ AVISOS ============

  async getAvisos() {
    return this.request('/api/admin/avisos');
  },

  async criarAviso(data) {
    return this.request('/api/admin/avisos', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async atualizarAviso(id, data) {
    return this.request(`/api/admin/avisos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async excluirAviso(id) {
    return this.request(`/api/admin/avisos/${id}`, {
      method: 'DELETE'
    });
  },

  async getAvisosNaoLidos() {
    return this.request('/api/avisos/nao-lidos');
  },

  async marcarAvisoLido(id) {
    return this.request(`/api/avisos/${id}/lido`, {
      method: 'POST'
    });
  },

  // ============ ASSETS (ADMIN) ============

  async getAssets(prefix = '') {
    return this.request(`/api/admin/assets/list?prefix=${encodeURIComponent(prefix)}`);
  },

  async uploadAsset(file, folder = 'general', customName = null) {
    const token = Storage.get('authToken', null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    if (customName) formData.append('name', customName);

    const response = await fetch(`${API_BASE_URL}/api/admin/assets/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro no upload de asset' }));
      throw new Error(error.error);
    }

    return response.json();
  },

  async deleteAsset(key) {
    return this.request(`/api/admin/assets?key=${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
  }
};

// Flag para usar API ou dados locais (fallback)
export const USE_API = true;

export default API;
